import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, X, Send, Bot, User, ShoppingCart, 
  Sparkles, ChevronUp, Loader2, ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NutritionChatWidget({ forceOpen = false }) {
  const { user } = useAuth();
  const { addItem, itemCount } = useCart();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [products, setProducts] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);

  // Auto-open for new users
  useEffect(() => {
    if (user && !hasShownWelcome) {
      const welcomeShown = localStorage.getItem(`chat_welcome_${user.user_id}`);
      if (!welcomeShown) {
        // First time user - open chat after a delay
        const timer = setTimeout(() => {
          setIsOpen(true);
          setMessages([{
            role: 'assistant',
            content: `¡Hola ${user.name?.split(' ')[0] || 'bienvenido'}! Soy tu nutrióloga AI de Lunch & Roll. 🥗

Estoy aquí para ayudarte a:
• Encontrar comidas según tus preferencias
• Recomendarte opciones saludables
• Agregar productos a tu carrito (solo dímelo)
• Crear pedidos recurrentes ("quiero esto todos los martes")

¿En qué puedo ayudarte hoy?`
          }]);
          setHasShownWelcome(true);
          localStorage.setItem(`chat_welcome_${user.user_id}`, 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, hasShownWelcome]);

  // Force open from external control
  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (user) {
      checkProfile();
      fetchProducts();
      loadChatHistory();
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const checkProfile = async () => {
    try {
      const res = await fetch(`${API}/nutrition/profile`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHasProfile(data && data.weight_kg);
      }
    } catch (e) {
      console.error('Error checking profile:', e);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/products`);
      if (res.ok) {
        setProducts(await res.json());
      }
    } catch (e) {
      console.error('Error fetching products:', e);
    }
  };

  const loadChatHistory = async () => {
    try {
      const res = await fetch(`${API}/nutrition/chat/history`, { credentials: 'include' });
      if (res.ok) {
        const history = await res.json();
        // Convert history to messages format (most recent last)
        const msgs = [];
        history.reverse().slice(-10).forEach(h => {
          msgs.push({ role: 'user', content: h.user_message });
          msgs.push({ role: 'assistant', content: h.assistant_response, items: h.suggested_items });
        });
        setMessages(msgs);
      }
    } catch (e) {
      console.error('Error loading chat history:', e);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/nutrition/assistant-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response,
          items: data.suggested_items || [],
          recurringOrder: data.recurring_order,
          action: data.action
        }]);
        
        // If there are suggested items, store them as pending
        if (data.suggested_items && data.suggested_items.length > 0) {
          setPendingItems(data.suggested_items);
        }

        // Handle direct actions
        if (data.action === 'added_to_cart' && data.added_items) {
          data.added_items.forEach(item => {
            const product = products.find(p => p.product_id === item.product_id);
            if (product) {
              addItem(product, item.quantity || 1);
            }
          });
          toast.success(`${data.added_items.length} producto(s) agregado(s) al carrito`);
        }
      } else {
        throw new Error('Error en respuesta');
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, tuve un problema. ¿Podrías intentar de nuevo?' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const addSuggestedToCart = (items) => {
    items.forEach(item => {
      const product = products.find(p => p.product_id === item.product_id);
      if (product) {
        addItem(product, item.quantity || 1);
      }
    });
    toast.success(`${items.length} producto(s) agregado(s) al carrito`);
    setPendingItems([]);
  };

  const createRecurringOrder = async (recurringOrder) => {
    try {
      const res = await fetch(`${API}/recurring-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: recurringOrder.name,
          items: recurringOrder.items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
          frequency: recurringOrder.frequency,
          day_of_week: recurringOrder.day_of_week,
          delivery_time: '12:00'
        })
      });

      if (res.ok) {
        toast.success(`Pedido recurrente "${recurringOrder.name}" creado`);
      } else {
        throw new Error('Error al crear');
      }
    } catch (error) {
      console.error('Error creating recurring order:', error);
      toast.error('Error al crear el pedido recurrente');
    }
  };

  const dayNames = {
    monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
    thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
  };

  const frequencyNames = {
    weekly: 'Semanal', daily: 'Diario', biweekly: 'Cada 2 semanas'
  };

  const goToCart = () => {
    setIsOpen(false);
    navigate('/cart');
  };

  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 rounded-full shadow-2xl hover:shadow-emerald-500/25 hover:scale-105 transition-all duration-300 group"
          data-testid="nutrition-chat-button"
        >
          <div className="relative">
            <Bot className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
          </div>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-stone-900 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            Nutricionista IA
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden transition-all duration-300 ${
            isMinimized ? 'w-72 h-14' : 'w-96 h-[32rem]'
          }`}
          data-testid="nutrition-chat-widget"
        >
          {/* Header */}
          <div 
            className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 flex items-center justify-between cursor-pointer"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">Nutricionista IA</p>
                {!isMinimized && (
                  <p className="text-xs text-emerald-100">Tu asistente de comida saludable</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {itemCount > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); goToCart(); }}
                  className="bg-white/20 hover:bg-white/30 p-1.5 rounded-lg transition-colors relative"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <Badge className="absolute -top-1 -right-1 w-4 h-4 p-0 flex items-center justify-center bg-orange-500 text-white text-xs border-0">
                    {itemCount}
                  </Badge>
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                className="hover:bg-white/20 p-1 rounded transition-colors"
              >
                <ChevronUp className={`w-5 h-5 transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                className="hover:bg-white/20 p-1 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages Area */}
              <div className="h-80 overflow-y-auto p-4 space-y-4 bg-stone-50">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="font-medium text-stone-900 mb-2">¡Hola! Soy tu nutricionista</p>
                    <p className="text-sm text-stone-500 mb-4">
                      Puedo ayudarte a elegir comidas saludables y agregar productos a tu carrito
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => setInput('¿Qué me recomiendas para hoy?')}
                        className="w-full text-left text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 hover:border-emerald-300 transition-colors"
                      >
                        💡 ¿Qué me recomiendas para hoy?
                      </button>
                      <button
                        onClick={() => setInput('Quiero un almuerzo bajo en calorías')}
                        className="w-full text-left text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 hover:border-emerald-300 transition-colors"
                      >
                        🥗 Quiero un almuerzo bajo en calorías
                      </button>
                      <button
                        onClick={() => setInput('Arma mi pedido para mañana')}
                        className="w-full text-left text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 hover:border-emerald-300 transition-colors"
                      >
                        🛒 Arma mi pedido para mañana
                      </button>
                      <button
                        onClick={() => setInput('Quiero lo mismo todos los martes')}
                        className="w-full text-left text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 hover:border-purple-300 transition-colors"
                      >
                        🔄 Crear pedido recurrente semanal
                      </button>
                    </div>
                    {!hasProfile && (
                      <button
                        onClick={() => navigate('/settings/nutrition')}
                        className="mt-4 text-sm text-emerald-600 hover:underline"
                      >
                        Configura tu perfil para recomendaciones personalizadas →
                      </button>
                    )}
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                      <div className={`rounded-2xl px-4 py-2.5 ${
                        msg.role === 'user' 
                          ? 'bg-emerald-600 text-white rounded-br-md' 
                          : 'bg-white border border-stone-200 text-stone-800 rounded-bl-md shadow-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      
                      {/* Suggested Items */}
                      {msg.items && msg.items.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {msg.items.map((item, j) => (
                            <div key={j} className="bg-white border border-emerald-200 rounded-xl p-3 shadow-sm">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-stone-900 text-sm">{item.name}</p>
                                  <p className="text-xs text-stone-500">${item.price?.toFixed(2)} MXN</p>
                                </div>
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                  x{item.quantity || 1}
                                </Badge>
                              </div>
                            </div>
                          ))}
                          <Button
                            size="sm"
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                            onClick={() => addSuggestedToCart(msg.items)}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Agregar al carrito
                          </Button>
                        </div>
                      )}

                      {/* Recurring Order Suggestion */}
                      {msg.recurringOrder && (
                        <div className="mt-2 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-3 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-purple-900 text-sm">{msg.recurringOrder.name}</p>
                              <p className="text-xs text-purple-600">
                                {frequencyNames[msg.recurringOrder.frequency]} · {dayNames[msg.recurringOrder.day_of_week]}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-1 mb-3">
                            {msg.recurringOrder.items?.map((item, j) => (
                              <div key={j} className="flex justify-between text-sm">
                                <span className="text-purple-800">{item.name} x{item.quantity}</span>
                                <span className="text-purple-600">${item.price?.toFixed(2)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm font-medium pt-1 border-t border-purple-200">
                              <span className="text-purple-900">Total semanal</span>
                              <span className="text-purple-700">${msg.recurringOrder.total?.toFixed(2)}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                            onClick={() => createRecurringOrder(msg.recurringOrder)}
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Activar pedido recurrente
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                        <span className="text-sm text-stone-500">Pensando...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input Area */}
              <form onSubmit={sendMessage} className="p-3 bg-white border-t border-stone-100">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Pregúntame sobre comida..."
                    className="flex-1 rounded-xl border-stone-200 focus:border-emerald-500 focus:ring-emerald-500"
                    disabled={loading}
                    data-testid="nutrition-chat-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    disabled={loading || !input.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-xl shrink-0"
                    data-testid="nutrition-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                {itemCount > 0 && (
                  <button
                    type="button"
                    onClick={goToCart}
                    className="w-full mt-2 text-sm text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1"
                  >
                    Ver carrito ({itemCount} productos) <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
